import { DirectUpload, DirectUploadDelegate } from '@rails/activestorage';

/**
 * Direct upload de arquivos das respostas do caderno de campo.
 *
 * Fluxo (AGROS-3304 / AGROS-3390):
 *  1. POST /field_notebook/direct_uploads (JWT) com filename, byte_size,
 *     checksum MD5 (base64) e content_type -> `signed_id` + URL assinada + headers;
 *  2. PUT do arquivo cru direto no storage (S3) com exatamente os headers devolvidos;
 *  3. o `signed_id` vai no `value` da resposta no lugar do base64.
 *
 * Os passos 1 e 2 são feitos pelo cliente oficial do Active Storage
 * (`@rails/activestorage`), que já calcula o checksum e monta o PUT sem
 * headers extras (Authorization no PUT invalidaria a assinatura da URL).
 */

export const DIRECT_UPLOAD_PATH = '/field_notebook/direct_uploads';

export const ALLOWED_CONTENT_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'application/pdf',
];

export const MAX_FILE_BYTES = 25 * 1024 * 1024;

export type DirectUploadStep = 'blob' | 'storage' | 'aborted';

export class DirectUploadError extends Error {
  step: DirectUploadStep;

  status: number;

  constructor(message: string, step: DirectUploadStep, status: number) {
    super(message);
    this.name = 'DirectUploadError';
    this.step = step;
    this.status = status;
    Object.setPrototypeOf(this, DirectUploadError.prototype);
  }

  /** URL assinada expirada ou rejeitada pelo storage: refazer o passo 1 resolve. */
  get isRetryable(): boolean {
    return this.step === 'storage' && [0, 400, 403].includes(this.status);
  }
}

export interface DirectUploadResult {
  signedId: string;
  filename: string;
  contentType: string;
  byteSize: number;
}

export interface DirectUploadOptions {
  jwt: string;
  onProgress?: (percent: number) => void;
  /** Cancela o upload em andamento (o item some da resposta). */
  signal?: AbortSignal;
  /** Sobrescreve a URL do endpoint (testes). */
  url?: string;
  /** Tentativas extras quando o storage rejeita a URL assinada. */
  retries?: number;
}

export function getDirectUploadUrl(): string {
  const base = (process.env.REACT_APP_FIELD_NOTEBOOK_API_BASE_URL ?? '').replace(
    /\/+$/,
    '',
  );
  return `${base}${DIRECT_UPLOAD_PATH}`;
}

export function parseDirectUploadError(error: unknown): DirectUploadError {
  if (error instanceof DirectUploadError) return error;

  const message = error instanceof Error ? error.message : String(error);
  const status = Number(/Status: (\d+)/.exec(message)?.[1] ?? 0);
  const step: DirectUploadStep = message.startsWith('Error storing') ? 'storage' : 'blob';

  return new DirectUploadError(message, step, status);
}

function uploadOnce(file: File, options: DirectUploadOptions): Promise<DirectUploadResult> {
  const { jwt, onProgress, signal, url } = options;

  return new Promise((resolve, reject) => {
    let settled = false;
    const settle = (fn: () => void) => {
      if (settled) return;
      settled = true;
      fn();
    };

    const abortWith = (xhr: XMLHttpRequest) => {
      if (!signal) return;
      const onAbort = () => {
        xhr.abort();
        settle(() => reject(new DirectUploadError('Upload aborted', 'aborted', 0)));
      };
      if (signal.aborted) {
        onAbort();
        return;
      }
      signal.addEventListener('abort', onAbort, { once: true });
      xhr.addEventListener('loadend', () => signal.removeEventListener('abort', onAbort));
    };

    const delegate: DirectUploadDelegate = {
      directUploadWillCreateBlobWithXHR(xhr) {
        xhr.setRequestHeader('Authorization', `Bearer ${jwt}`);
        xhr.setRequestHeader('platform', 'web');
        abortWith(xhr);
      },
      directUploadWillStoreFileWithXHR(xhr) {
        abortWith(xhr);
        if (!onProgress) return;
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable && event.total > 0) {
            onProgress(Math.round((event.loaded / event.total) * 100));
          }
        });
      },
    };

    const upload = new DirectUpload(file, url ?? getDirectUploadUrl(), delegate);

    upload.create((error, blob) => {
      if (signal?.aborted) {
        settle(() => reject(new DirectUploadError('Upload aborted', 'aborted', 0)));
        return;
      }

      if (error || !blob) {
        settle(() => reject(parseDirectUploadError(error ?? 'Error creating Blob')));
        return;
      }

      onProgress?.(100);
      settle(() =>
        resolve({
          signedId: blob.signed_id,
          filename: blob.filename,
          contentType: blob.content_type,
          byteSize: blob.byte_size,
        }),
      );
    });
  });
}

/**
 * Envia um arquivo por direct upload e devolve o `signed_id` a ser usado no
 * `value` da resposta. Se o storage rejeitar a URL assinada (expirou), refaz
 * o fluxo do zero uma vez.
 */
export async function directUploadFile(
  file: File,
  options: DirectUploadOptions,
): Promise<DirectUploadResult> {
  const retries = options.retries ?? 1;
  let attempt = 0;

  for (;;) {
    if (options.signal?.aborted) {
      throw new DirectUploadError('Upload aborted', 'aborted', 0);
    }

    try {
      // eslint-disable-next-line no-await-in-loop
      return await uploadOnce(file, options);
    } catch (error) {
      const uploadError = parseDirectUploadError(error);
      if (!uploadError.isRetryable || attempt >= retries) throw uploadError;
      attempt += 1;
      options.onProgress?.(0);
    }
  }
}

export type FileValidationError = 'unsupported_file_type' | 'max_image_size_error_text';

export function inferContentType(file: File): string {
  if (file.type) return file.type;
  const extension = file.name.split('.').pop()?.toLowerCase();
  const byExtension: Record<string, string> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    webp: 'image/webp',
    pdf: 'application/pdf',
  };
  return (extension && byExtension[extension]) || '';
}

export function isAllowedContentType(contentType: string): boolean {
  return ALLOWED_CONTENT_TYPES.includes(contentType);
}

/** Valida tipo e tamanho antes de qualquer request. `null` = arquivo ok. */
export function validateUploadFile(file: File): FileValidationError | null {
  const contentType = inferContentType(file);
  const isImage = contentType.startsWith('image/');

  if (!isImage && !isAllowedContentType(contentType)) return 'unsupported_file_type';
  if (file.size <= 0 || file.size > MAX_FILE_BYTES) return 'max_image_size_error_text';

  return null;
}

/** Chave de tradução da mensagem exibida para um erro de upload. */
export function uploadErrorKey(error: unknown): string {
  if (error instanceof DirectUploadError) {
    if (error.step === 'blob' && error.status === 422) return 'unsupported_file_type';
    return 'upload_failed';
  }

  const message = error instanceof Error ? error.message : String(error);
  return message.startsWith('Unsupported content type') ? 'unsupported_file_type' : 'upload_failed';
}
